import {
  CallTask,
  HubspotOrgIntegrationData,
  SalesforceOrgIntegrationData,
  SourceType,
} from 'types';
import { OrgIntegrationService } from 'sb-lib/types';
import { OrgIntegration } from 'types/integration';
import { IconName } from 'shared/components/Icon';
import { startsWith } from 'lodash';

export type CallServiceName =
  | 'hubspot'
  | 'outreach'
  | 'linkedin'
  | 'google'
  | 'apollo'
  | 'salesloft'
  | 'salesforce';

const SOURCE_TYPE_TO_NAME: Record<SourceType, CallServiceName | null> = {
  [SourceType.APOLLO_SEQUENCE]: 'apollo',
  [SourceType.APOLLO_TASK]: 'apollo',
  [SourceType.HUBSPOT_LIST]: 'hubspot',
  [SourceType.HUBSPOT_TASK]: 'hubspot',
  [SourceType.OUTREACH_SEQUENCE]: 'outreach',
  [SourceType.OUTREACH_TASK]: 'outreach',
  [SourceType.SALESFORCE_REPORT]: 'salesforce',
  [SourceType.SALESLOFT_CADENCE]: 'salesloft',
  [SourceType.SALESLOFT_TASK]: 'salesloft',
  [SourceType.SERVICEBELL_LIST]: null,
  [SourceType.CONTACT]: null,
  [SourceType.DIALPAD]: null,
};

const SERVICE_ICONS: Record<CallServiceName, IconName> = {
  hubspot: 'hubspot',
  outreach: 'outreach',
  linkedin: 'linkedinFilled',
  google: 'google',
  apollo: 'apollo',
  salesloft: 'salesloft',
  salesforce: 'salesforce',
};

export const getSequencerExternalContactUrl = (
  task: CallTask,
  orgIntegrations: OrgIntegration[],
) => {
  const name = SOURCE_TYPE_TO_NAME[task.sourceType];
  if (!name) return null;

  return getExternalContactUrl(task, name, orgIntegrations);
};

export const getExternalContactUrl = (
  task: CallTask,
  serviceName: CallServiceName,
  orgIntegrations: OrgIntegration[],
) => {
  const contactId = task.externalContactId ?? task.sourceId;
  const hsPortalId = (orgIntegrations.find(
    o => o.service === OrgIntegrationService.hubspot,
  )?.data as HubspotOrgIntegrationData)?.portalId;
  const instanceUrl = (orgIntegrations.find(
    o => o.service == OrgIntegrationService.salesforce,
  )?.data as SalesforceOrgIntegrationData)?.instanceUrl;

  const name = (task.contact?.name ?? '').trim();
  const query = encodeURI(`${name ?? ''} ${task.contact?.company?.name ?? ''}`.trim());

  if (
    (serviceName === 'hubspot' && !hsPortalId) ||
    (serviceName === 'linkedin' && !query) ||
    (serviceName === 'google' && !query)
  ) {
    return null;
  }

  const SERVICE_LINKS: Record<CallServiceName, string> = {
    hubspot: `https://app.hubspot.com/contacts/${hsPortalId}/${contactId}`,
    outreach: `https://web.outreach.io/prospects/${contactId}/overview`,
    linkedin: `https://www.linkedin.com/search/results/people?keywords=${query}`,
    google: `https://www.google.com/search?q=${query}`,
    apollo: `https://app.apollo.io/#/contacts/${contactId}`,
    salesloft: `https://app.salesloft.com/app/people/${contactId}`,
    salesforce: `${instanceUrl}`,
  };

  if (serviceName === 'salesforce') {
    const objectType = startsWith(contactId.toString(), '003') ? 'Contact' : 'Lead';
    return `${SERVICE_LINKS[serviceName]}/lightning/r/${objectType}/${contactId}/view`;
  }

  return SERVICE_LINKS[serviceName];
};

export const getExternalServiceIconName = (serviceName: CallServiceName) => {
  return SERVICE_ICONS[serviceName];
};
